import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import type { UserRole, Organization } from '../types';
import { Users, Building2, Plus, X, Trash2, Pencil, Upload, Eye } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const ROLE_I18N_KEY: Record<UserRole, string> = {
  admin: 'admin.roleAdmin',
  agent: 'admin.roleAgent',
  customer_manager: 'admin.roleManager',
  customer_viewer: 'admin.roleViewer',
};

export default function AdminPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'users' | 'organizations'>('users');

  return (
    <div className="space-y-6">
      <h1 className="page-title">{t('admin.title')}</h1>

      <div className="flex gap-2 border-b border-surface-400/10">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'users' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-surface-500 hover:text-surface-700'
          }`}
          onClick={() => setActiveTab('users')}
        >
          <span className="flex items-center gap-2"><Users size={18} /> {t('admin.users')}</span>
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'organizations' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-surface-500 hover:text-surface-700'
          }`}
          onClick={() => setActiveTab('organizations')}
        >
          <span className="flex items-center gap-2"><Building2 size={18} /> {t('admin.organizations')}</span>
        </button>
      </div>

      {activeTab === 'users' ? <UsersTab /> : <OrganizationsTab />}
    </div>
  );
}

function UsersTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ limit: 100 }),
  });

  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.getOrganizations(),
  });

  const organizations = orgsData?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const roleColors: Record<string, string> = {
    admin: 'bg-danger-100 text-red-800',
    agent: 'bg-info-100 text-blue-800',
    customer_manager: 'bg-purple-100 text-purple-800',
    customer_viewer: 'bg-surface-100 text-surface-800',
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {isAdmin && (
          <button className="btn btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)}>
            <Upload size={18} /> <span>{t('admin.importUsers')}</span>
          </button>
        )}
        <button className="btn btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)}>
          <Plus size={18} /> <span>{t('admin.newUser')}</span>
        </button>
      </div>

      {showCreate && (
        <CreateUserModal organizations={organizations} onClose={() => setShowCreate(false)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['users'] }); setShowCreate(false); }} />
      )}

      {showImport && (
        <ImportUsersModal organizations={organizations} onClose={() => setShowImport(false)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['users'] }); setShowImport(false); }} />
      )}

      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['users'] }); setEditingUser(null); }} />
      )}

      {isLoading && <div className="flex justify-center py-12"><div className="spinner"></div></div>}

      {data?.success && (
        <div className="grid gap-3">
          {data.data && data.data.length > 0 ? data.data.map((u) => (
            <div key={u.id} className="card flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-sm">{u.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-medium text-surface-900">{u.name}</p>
                  <p className="text-sm text-surface-500">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-surface-500">{u.organization_name}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleColors[u.role] || 'bg-surface-100'}`}>
                  {t(ROLE_I18N_KEY[u.role as UserRole])}
                </span>
                <span className={`px-2 py-1 rounded text-xs ${u.active ? 'bg-success-100 text-success-700' : 'bg-danger-100 text-danger-700'}`}>
                  {u.active ? t('common.active') : t('common.disabled')}
                </span>
                {(isAdmin || (u.role !== 'admin' && u.role !== 'agent')) && (
                  <button onClick={() => setEditingUser(u)} className="p-1 text-surface-400 hover:text-info-600 transition-colors">
                    <Pencil size={16} />
                  </button>
                )}
                {isAdmin && u.role !== 'admin' && u.id !== currentUser?.id && (
                  <button onClick={async () => {
                    try {
                      const originalToken = localStorage.getItem('token');
                      if (originalToken) localStorage.setItem('tsi_original_token', originalToken);
                      const res = await api.impersonateUser(u.id);
                      if (res.data) {
                        localStorage.setItem('token', res.data.token);
                        localStorage.setItem('tsi_impersonating', 'true');
                        window.location.href = '/';
                      }
                    } catch { /* impersonation error handled by API */ }
                  }}
                    className="p-1 text-surface-400 hover:text-purple-600 transition-colors" title={t('admin.impersonate')}>
                    <Eye size={16} />
                  </button>
                )}
                {(isAdmin || (u.role !== 'admin' && u.role !== 'agent')) && (
                  <button onClick={() => setConfirmDeleteUser({ id: u.id, name: u.name })}
                    className="p-1 text-surface-400 hover:text-danger-600 transition-colors">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          )) : (
            <div className="card text-center py-12">
              <Users className="mx-auto text-surface-400 mb-4" size={48} />
              <p className="text-surface-600">{t('admin.noUsers')}</p>
            </div>
          )}
        </div>
      )}

      {confirmDeleteUser && (
        <ConfirmModal
          title={t('admin.deactivateUser')}
          description={`${t('admin.deactivateConfirm')}${confirmDeleteUser.name}?`}
          confirmLabel={t('admin.deactivate')}
          onConfirm={() => { deleteMutation.mutate(confirmDeleteUser.id); setConfirmDeleteUser(null); }}
          onCancel={() => setConfirmDeleteUser(null)}
          danger
        />
      )}
    </div>
  );
}

function CreateUserModal({ organizations, onClose, onSuccess }: {
  organizations: Organization[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const isAgent = currentUser?.role === 'agent';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('customer_viewer');
  const [orgId, setOrgId] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.createUser({
      organization_id: orgId,
      role,
      name,
      email,
      password,
      phone: phone || undefined,
      phone_extension: phoneExtension || undefined,
    }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('admin.newUser')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('billing.org')}</label>
            <select className="input" value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
              <option value="">{t('admin.selectOrg')}</option>
              {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div><label className="label">{t('common.name')}</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></div>
          <div><label className="label">{t('common.email')}</label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div><label className="label">{t('common.password')}</label><input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} pattern="(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}" title={t('admin.passwordRules')} /></div>
          <div>
            <label className="label">{t('common.role')}</label>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              {!isAgent && <option value="admin">{t('admin.roleAdmin')}</option>}
              {!isAgent && <option value="agent">{t('admin.roleAgent')}</option>}
              <option value="customer_manager">{t('admin.roleManager')}</option>
              <option value="customer_viewer">{t('admin.roleViewer')}</option>
            </select>
            {isAgent && <p className="text-xs text-surface-500 mt-1">{t('admin.agentCantCreateStaff')}</p>}
          </div>
          <div><label className="label">{t('admin.phoneOptional')}</label><input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          {(role === 'admin' || role === 'agent') && (
            <div>
              <label className="label">{t('admin.phoneExtensionLabel')}</label>
              <input
                className="input"
                value={phoneExtension}
                onChange={(e) => setPhoneExtension(e.target.value)}
                placeholder={t('admin.phoneExtensionPlaceholder')}
                maxLength={20}
              />
              <p className="text-xs text-surface-500 mt-1">{t('admin.phoneExtensionHint')}</p>
            </div>
          )}
          {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{(mutation.error as any)?.error?.message || t('admin.createUserError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">{mutation.isPending ? t('common.creating') : t('admin.createUser')}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSuccess }: { user: any; onClose: () => void; onSuccess: () => void }) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const isAgent = currentUser?.role === 'agent';
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [phone, setPhone] = useState(user.phone || '');
  const [phoneExtension, setPhoneExtension] = useState(user.phone_extension || '');
  const [active, setActive] = useState(user.active);
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const data: any = { name, email, role, phone, active, phone_extension: phoneExtension || null };
      if (password) data.password = password;
      return api.updateUser(user.id, data);
    },
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('admin.editUser')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div><label className="label">{t('common.name')}</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></div>
          <div><label className="label">{t('common.email')}</label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div><label className="label">{t('admin.newPassword')}</label><input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} /></div>
          <div>
            <label className="label">{t('common.role')}</label>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              {!isAgent && <option value="admin">{t('admin.roleAdmin')}</option>}
              {!isAgent && <option value="agent">{t('admin.roleAgent')}</option>}
              <option value="customer_manager">{t('admin.roleManager')}</option>
              <option value="customer_viewer">{t('admin.roleViewer')}</option>
            </select>
          </div>
          <div><label className="label">{t('common.phone')}</label><input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          {(role === 'admin' || role === 'agent') && (
            <div>
              <label className="label">{t('admin.phoneExtensionLabel')}</label>
              <input
                className="input"
                value={phoneExtension}
                onChange={(e) => setPhoneExtension(e.target.value)}
                placeholder={t('admin.phoneExtensionPlaceholder')}
                maxLength={20}
              />
              <p className="text-xs text-surface-500 mt-1">{t('admin.phoneExtensionHint')}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="user-active" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-4 h-4" />
            <label htmlFor="user-active" className="text-sm text-surface-700">{t('common.active')}</label>
          </div>
          {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{(mutation.error as any)?.error?.message || t('admin.updateUserError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">{mutation.isPending ? t('common.updating') : t('common.update')}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OrganizationsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';
  const [showCreate, setShowCreate] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [confirmDeactivateOrg, setConfirmDeactivateOrg] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.getOrganizations(),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.updateOrganization(id, { active: false }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organizations'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className="btn btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)}>
          <Plus size={18} /> <span>{t('admin.newOrg')}</span>
        </button>
      </div>

      {showCreate && (
        <CreateOrgModal onClose={() => setShowCreate(false)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['organizations'] }); setShowCreate(false); }} />
      )}

      {editingOrg && (
        <EditOrgModal org={editingOrg} onClose={() => setEditingOrg(null)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['organizations'] }); setEditingOrg(null); }} />
      )}

      {isLoading && <div className="flex justify-center py-12"><div className="spinner"></div></div>}

      {data?.success && (
        <div className="grid gap-3">
          {data.data && data.data.length > 0 ? data.data.map((org) => (
            <div key={org.id} className="card flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <Building2 className="text-primary-600" size={24} />
                </div>
                <div>
                  <p className="font-medium text-surface-900">{org.name}</p>
                  <div className="flex flex-wrap gap-x-3 text-sm text-surface-500">
                    <span>
                      {org.billing_model === 'hourly' ? t('admin.hourly') : t('admin.retainer')}
                      {org.billing_model === 'hourly' && org.hourly_rate > 0 && ` - ${org.hourly_rate}${t('admin.shekelPerHour')}`}
                      {org.billing_model === 'retainer' && org.retainer_hours && org.retainer_hours > 0 && ` - ${org.retainer_hours}${t('admin.hoursUnit')}`}
                    </span>
                    {org.tax_id && <span>| {t('admin.taxId')}: {org.tax_id}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-surface-400">
                    {org.phone && <span>{org.phone}</span>}
                    {org.email && <span>{org.email}</span>}
                    {org.city && <span>{org.city}</span>}
                  </div>
                  {org.account_manager_name && (
                    <p className="text-xs text-primary-600">{t('admin.accountManager')}: {org.account_manager_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs ${org.active ? 'bg-success-100 text-success-700' : 'bg-danger-100 text-danger-700'}`}>
                  {org.active ? t('common.active') : t('common.disabled')}
                </span>
                <button onClick={() => setEditingOrg(org)} className="p-1 text-surface-400 hover:text-info-600 transition-colors">
                  <Pencil size={16} />
                </button>
                {org.active && isAdmin && (
                  <button onClick={() => setConfirmDeactivateOrg({ id: org.id, name: org.name })}
                    className="p-1 text-surface-400 hover:text-danger-600 transition-colors">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          )) : (
            <div className="card text-center py-12">
              <Building2 className="mx-auto text-surface-400 mb-4" size={48} />
              <p className="text-surface-600">{t('admin.noOrgs')}</p>
            </div>
          )}
        </div>
      )}

      {confirmDeactivateOrg && (
        <ConfirmModal
          title={t('admin.deactivateOrg')}
          description={`${t('admin.deactivateOrgConfirm')}${confirmDeactivateOrg.name}?`}
          confirmLabel={t('admin.deactivateOrgBtn')}
          onConfirm={() => { deactivateMutation.mutate(confirmDeactivateOrg.id); setConfirmDeactivateOrg(null); }}
          onCancel={() => setConfirmDeactivateOrg(null)}
          danger
        />
      )}
    </div>
  );
}

function CreateOrgModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgPhone, setOrgPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [billingModel, setBillingModel] = useState('hourly');
  const [hourlyRate, setHourlyRate] = useState('');
  const [retainerHours, setRetainerHours] = useState('');
  const [accountManagerId, setAccountManagerId] = useState('');

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ limit: 200 }),
  });
  const agents = (usersData?.data ?? []).filter((u: any) => (u.role === 'admin' || u.role === 'agent') && u.active);

  const mutation = useMutation({
    mutationFn: () => api.createOrganization({
      name, billing_model: billingModel,
      hourly_rate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      retainer_hours: retainerHours ? parseInt(retainerHours) : undefined,
      account_manager_id: accountManagerId || null,
      tax_id: taxId || undefined,
      email: orgEmail || undefined,
      phone: orgPhone || undefined,
      address: address || undefined,
      city: city || undefined,
      website: website || undefined,
      notes: notes || undefined,
    }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('admin.newOrg')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-5">
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-surface-700 mb-2">{t('admin.orgDetails')}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="label">{t('admin.orgName')}</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></div>
              <div><label className="label">{t('admin.taxId')}</label><input className="input" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="51-1234567" /></div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-surface-700 mb-2">{t('admin.orgContact')}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="label">{t('admin.orgEmail')}</label><input className="input" type="email" value={orgEmail} onChange={(e) => setOrgEmail(e.target.value)} /></div>
              <div><label className="label">{t('admin.orgPhone')}</label><input className="input" type="tel" value={orgPhone} onChange={(e) => setOrgPhone(e.target.value)} /></div>
              <div><label className="label">{t('admin.orgAddress')}</label><input className="input" value={address} onChange={(e) => setAddress(e.target.value)} /></div>
              <div><label className="label">{t('admin.orgCity')}</label><input className="input" value={city} onChange={(e) => setCity(e.target.value)} /></div>
              <div className="md:col-span-2"><label className="label">{t('admin.orgWebsite')}</label><input className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" /></div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-surface-700 mb-2">{t('admin.billingModel')}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">{t('admin.billingModel')}</label>
                <select className="input" value={billingModel} onChange={(e) => setBillingModel(e.target.value)}>
                  <option value="hourly">{t('admin.hourly')}</option><option value="retainer">{t('admin.retainer')}</option>
                </select>
              </div>
              {billingModel === 'hourly' && (
                <div><label className="label">{t('admin.hourlyRate')}</label><input className="input" type="number" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} /></div>
              )}
              {billingModel === 'retainer' && (
                <div><label className="label">{t('admin.retainerHours')}</label><input className="input" type="number" value={retainerHours} onChange={(e) => setRetainerHours(e.target.value)} /></div>
              )}
            </div>
            <div>
              <label className="label">{t('admin.accountManager')}</label>
              <select className="input" value={accountManagerId} onChange={(e) => setAccountManagerId(e.target.value)}>
                <option value="">{t('admin.noAccountManager')}</option>
                {agents.map((u: any) => <option key={u.id} value={u.id}>{u.name} ({t(ROLE_I18N_KEY[u.role as UserRole])})</option>)}
              </select>
            </div>
          </fieldset>

          <div>
            <label className="label">{t('admin.orgNotes')}</label>
            <textarea className="input min-h-[60px]" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>

          {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('admin.createOrgError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">{mutation.isPending ? t('common.creating') : t('admin.createOrg')}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditOrgModal({ org, onClose, onSuccess }: { org: Organization; onClose: () => void; onSuccess: () => void }) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';
  const [name, setName] = useState(org.name);
  const [taxId, setTaxId] = useState(org.tax_id || '');
  const [orgEmail, setOrgEmail] = useState(org.email || '');
  const [orgPhone, setOrgPhone] = useState(org.phone || '');
  const [address, setAddress] = useState(org.address || '');
  const [city, setCity] = useState(org.city || '');
  const [website, setWebsite] = useState(org.website || '');
  const [notes, setNotes] = useState(org.notes || '');
  const [billingModel, setBillingModel] = useState<string>(org.billing_model);
  const [hourlyRate, setHourlyRate] = useState(String(org.hourly_rate || ''));
  const [retainerHours, setRetainerHours] = useState(String(org.retainer_hours || ''));
  const [active, setActive] = useState(org.active);
  const [accountManagerId, setAccountManagerId] = useState(org.account_manager_id || '');

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ limit: 200 }),
  });
  const agents = (usersData?.data ?? []).filter((u: any) => (u.role === 'admin' || u.role === 'agent') && u.active);

  const mutation = useMutation({
    mutationFn: () => api.updateOrganization(org.id, {
      name, billing_model: billingModel, active,
      hourly_rate: hourlyRate ? parseFloat(hourlyRate) : 0,
      retainer_hours: retainerHours ? parseInt(retainerHours) : 0,
      account_manager_id: accountManagerId || null,
      tax_id: taxId,
      email: orgEmail,
      phone: orgPhone,
      address,
      city,
      website,
      notes,
    }),
    onSuccess,
  });

  return (
    <div className="modal-backdrop">
      <div className="modal-content max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-surface-900">{t('admin.editOrg')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-5">
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-surface-700 mb-2">{t('admin.orgDetails')}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="label">{t('admin.orgName')}</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></div>
              <div><label className="label">{t('admin.taxId')}</label><input className="input" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="51-1234567" /></div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-surface-700 mb-2">{t('admin.orgContact')}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="label">{t('admin.orgEmail')}</label><input className="input" type="email" value={orgEmail} onChange={(e) => setOrgEmail(e.target.value)} /></div>
              <div><label className="label">{t('admin.orgPhone')}</label><input className="input" type="tel" value={orgPhone} onChange={(e) => setOrgPhone(e.target.value)} /></div>
              <div><label className="label">{t('admin.orgAddress')}</label><input className="input" value={address} onChange={(e) => setAddress(e.target.value)} /></div>
              <div><label className="label">{t('admin.orgCity')}</label><input className="input" value={city} onChange={(e) => setCity(e.target.value)} /></div>
              <div className="md:col-span-2"><label className="label">{t('admin.orgWebsite')}</label><input className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" /></div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-surface-700 mb-2">{t('admin.billingModel')}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">{t('admin.billingModel')}</label>
                <select className="input" value={billingModel} onChange={(e) => setBillingModel(e.target.value)}>
                  <option value="hourly">{t('admin.hourly')}</option><option value="retainer">{t('admin.retainer')}</option>
                </select>
              </div>
              <div><label className="label">{t('admin.hourlyRate')}</label><input className="input" type="number" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} /></div>
              <div><label className="label">{t('admin.retainerHours')}</label><input className="input" type="number" value={retainerHours} onChange={(e) => setRetainerHours(e.target.value)} /></div>
            </div>
            <div>
              <label className="label">{t('admin.accountManager')}</label>
              <select className="input" value={accountManagerId} onChange={(e) => setAccountManagerId(e.target.value)}>
                <option value="">{t('admin.noAccountManager')}</option>
                {agents.map((u: any) => <option key={u.id} value={u.id}>{u.name} ({t(ROLE_I18N_KEY[u.role as UserRole])})</option>)}
              </select>
            </div>
          </fieldset>

          <div>
            <label className="label">{t('admin.orgNotes')}</label>
            <textarea className="input min-h-[60px]" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="org-active" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-4 h-4" />
              <label htmlFor="org-active" className="text-sm text-surface-700">{t('common.active')}</label>
            </div>
          )}
          {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('admin.updateOrgError')}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">{mutation.isPending ? t('common.updating') : t('common.update')}</button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportUsersModal({ organizations, onClose, onSuccess }: {
  organizations: Organization[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [orgId, setOrgId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [defaultRole, setDefaultRole] = useState('customer_viewer');
  const [result, setResult] = useState<{ created: number; skipped: number; total: number; details: any[] } | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      if (!file || !orgId) throw new Error('missing');
      return api.importUsers(orgId, file, defaultRole);
    },
    onSuccess: (res) => {
      if (res.data) setResult(res.data);
    },
  });

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-surface-900">{t('admin.importUsers')}</h3>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={20} /></button>
        </div>

        {!result ? (
          <div className="space-y-4">
            <div>
              <label className="label">{t('admin.organization')}</label>
              <select className="input" value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
                <option value="">{t('admin.selectOrg')}</option>
                {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">{t('admin.defaultRole')}</label>
              <select className="input" value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)}>
                <option value="customer_viewer">{t('admin.roleViewer')}</option>
                <option value="customer_manager">{t('admin.roleManager')}</option>
                <option value="agent">{t('admin.roleAgent')}</option>
              </select>
            </div>
            <div>
              <label className="label">{t('admin.csvFile')}</label>
              <input type="file" accept=".csv,.txt" className="input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <p className="text-xs text-surface-500 mt-1">{t('admin.csvFormat')}</p>
            </div>
            {mutation.error && <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">{t('admin.importError')}</div>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !file || !orgId}
                className="btn btn-primary flex-1">{mutation.isPending ? t('common.loading') : t('admin.startImport')}</button>
              <button onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-success-50 rounded-lg p-3 text-center border border-success-100">
                <div className="text-2xl font-bold text-success-700">{result.created}</div>
                <div className="text-xs text-success-600">{t('admin.importCreated')}</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center border border-orange-100">
                <div className="text-2xl font-bold text-orange-700">{result.skipped}</div>
                <div className="text-xs text-orange-600">{t('admin.importSkipped')}</div>
              </div>
              <div className="bg-info-50 rounded-lg p-3 text-center border border-info-100">
                <div className="text-2xl font-bold text-info-700">{result.total}</div>
                <div className="text-xs text-info-600">{t('admin.importTotal')}</div>
              </div>
            </div>
            {result.details.filter((d) => d.status !== 'created').length > 0 && (
              <div className="max-h-48 overflow-y-auto border border-surface-400/15 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50"><tr><th className="p-2 text-right">{t('common.row')}</th><th className="p-2 text-right">{t('admin.email')}</th><th className="p-2 text-right">{t('common.status')}</th></tr></thead>
                  <tbody>
                    {result.details.filter((d) => d.status !== 'created').map((d, i) => (
                      <tr key={i} className="border-t border-surface-400/10">
                        <td className="p-2">{d.row}</td>
                        <td className="p-2 text-surface-600">{d.email}</td>
                        <td className="p-2 text-danger-600">{d.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button onClick={onSuccess} className="btn btn-primary w-full">{t('common.close')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
